import React, { useState } from "react";
import { Icon } from "react-icons-kit";
import { iosArrowDown } from "react-icons-kit/ionicons/iosArrowDown";
import { iosArrowForward } from "react-icons-kit/ionicons/iosArrowForward";
import TestListItem from "./TestListItem";
import { getStatusColor, getStatusBGColor, STATUS_TEXT_COLORS, STATUS_BACKGROUND_COLORS, STATUS_DISPLAY_TEXT } from "../statusColors";
import { languageService } from "../../../Language/language.service";

const AssetCard = ({ asset, isNestedInPlan }) => {
  const [isExpanded, setIsExpanded] = useState(isNestedInPlan ? false : true);

  if (!asset) return null;

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getStatusPriority = (status) => {
    const priority = { overdue: 0, expiring: 1, due: 2, upcoming: 3 };
    return priority[status] !== undefined ? priority[status] : 3;
  };

  const sortTests = (testsArray) => {
    if (!Array.isArray(testsArray)) return [];
    return [...testsArray].sort((a, b) => {
      const pa = getStatusPriority(a.status);
      const pb = getStatusPriority(b.status);
      if (pa !== pb) return pa - pb;

      const ad = parseDate(a.expiryDate || a.dueDate);
      const bd = parseDate(b.expiryDate || b.dueDate);
      if (ad && bd) return ad - bd;
      if (ad) return -1;
      if (bd) return 1;

      if (a.title && b.title) return a.title.localeCompare(b.title);
      return 0;
    });
  };

  // Combine grouped and ungrouped tests for summary and listing
  const allTests = [
    ...(asset.unGroupedChildren || []),
    ...((asset.groupedChildren && asset.groupedChildren.flatMap((group) => group.tests)) || []),
  ];

  const sortedTests = sortTests(allTests);

  const testsByStatus = {
    overdue: sortedTests.filter((t) => t.status === "overdue"),
    expiring: sortedTests.filter((t) => t.status === "expiring"),
    due: sortedTests.filter((t) => t.status === "due"),
    upcoming: sortedTests.filter((t) => t.status === "upcoming"),
  };

  const groupedChildren =
    asset.groupedChildren && asset.groupedChildren.length > 0
      ? asset.groupedChildren.map((group) => ({
          ...group,
          tests: sortTests(group.tests),
        }))
      : [];

  const unGroupedChildren = sortTests(asset.unGroupedChildren || []);

  // Styling for nested vs standalone cards
  const cardStyle = isNestedInPlan
    ? {
        backgroundColor: "#f9f9f9",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        overflow: "hidden",
      }
    : {
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        borderRadius: "15px",
        padding: "15px",
        overflow: "hidden",
      };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          cursor: "pointer",
          paddingBottom: isExpanded ? "12px" : "0",
          borderBottom: isExpanded ? "1px solid #e0e0e0" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", flex: 1 }}>
          <Icon icon={isExpanded ? iosArrowDown : iosArrowForward} size={18} style={{ marginTop: "2px", flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: "0", fontSize: "14px", fontWeight: "600", color: "#333" }}>{asset.title}</h4>
            {asset.assetType && <p style={{ margin: "4px 0 0 0", color: "#666", fontSize: "11px" }}>{languageService(asset.assetType)}</p>}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap-reverse",
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          {/* Status badge */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "4px",
              backgroundColor: getStatusBGColor(asset.status),
              color: getStatusColor(asset.status),
              fontWeight: "600",
              fontSize: "10px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {STATUS_DISPLAY_TEXT[asset.status]}
          </div>

          {/* Test count summary - compact version */}
          {(testsByStatus.overdue.length > 0 ||
            testsByStatus.expiring.length > 0 ||
            testsByStatus.due.length > 0 ||
            testsByStatus.upcoming.length > 0) && (
            <div style={{ display: "flex", gap: "4px" }}>
              {testsByStatus.overdue.length > 0 && (
                <div
                  style={{
                    padding: "2px 6px",
                    backgroundColor: STATUS_BACKGROUND_COLORS.overdue,
                    color: STATUS_TEXT_COLORS.overdue,
                    borderRadius: "3px",
                    fontSize: "10px",
                    fontWeight: "600",
                  }}
                >
                  {testsByStatus.overdue.length}↑
                </div>
              )}
              {testsByStatus.expiring.length > 0 && (
                <div
                  style={{
                    padding: "2px 6px",
                    backgroundColor: STATUS_BACKGROUND_COLORS.expiring,
                    color: STATUS_TEXT_COLORS.expiring,
                    borderRadius: "3px",
                    fontSize: "10px",
                    fontWeight: "600",
                  }}
                >
                  {testsByStatus.expiring.length}⚠
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ paddingTop: "12px" }}>
          {/* Grouped Tests Sections */}
          {groupedChildren && groupedChildren.length > 0 && (
            <div style={{ marginBottom: "12px" }}>
              {groupedChildren.map((group, idx) => (
                <div key={idx} style={{ marginBottom: "12px" }}>
                  <h5
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#555",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #e0e0e0",
                      paddingBottom: "6px",
                    }}
                  >
                    {group.description || group.code}
                  </h5>
                  {group.tests && group.tests.length > 0 && (
                    <div>
                      {group.tests.map((test, testIdx) => (
                        <TestListItem key={testIdx} test={test} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Ungrouped Tests */}
          {unGroupedChildren && unGroupedChildren.length > 0 && (
            <div>
              {groupedChildren && groupedChildren.length > 0 && (
                <h5
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#555",
                    textTransform: "uppercase",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "6px",
                  }}
                >
                  Other Tests
                </h5>
              )}
              {unGroupedChildren.map((test, idx) => (
                <TestListItem key={idx} test={test} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {(!allTests || allTests.length === 0) && (
            <p style={{ color: "#999", fontSize: "12px", margin: "0", padding: "8px 0" }}>No tests available for this asset.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetCard;



// WEBPACK FOOTER //
// ./src/components/InspectionSchedule/components/AssetCard.jsx